<template>
  <div class="content-calendar-new-page">
    <!--<StepsProgressBar class="content-calendar-new-page__progressbar" :currentStep="activeStep" :numberOfSteps="totalNumberOfSteps" :fontSize="19" color="teal" />-->

    <div v-if="!isLoadingMatchedResults && noCalendarsFound" class="no-calendars-message" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <h2 class="global-h2">No Calendars Found</h2>
      <p class="global-p">Please contact Shrpa to get started.</p>
    </div>

    <StepCalendarPosts
      v-else
      class="content-calendar-new-page__step-container"
      :customerId="customerId"
      :flatBucketList="flatBucketList"
      :matchedResults="matchedResults"
      :futureCalendarSummary="futureCalendarSummary"
      :isLoading="isLoadingMatchedResults"
      :generationStatus="currentGenerationStatus"
      :isGeneratingPosts="isGeneratingPosts"
      :isRequestingPostsGeneration="isRequestingPostsGeneration"
      @requestPostsGeneration="async (specificItemId: string) => await handleRequestPostGenerationEvent(specificItemId)"
      @requestCalendarRegen="(userGuidance, deleteLiveCalendarPosts) => deleteAndRegenCalendar(userGuidance, deleteLiveCalendarPosts)"
      @postsUpdated="(updatedPostList, newWeekKey) => updatePostsLocally(updatedPostList, newWeekKey)"
      @postsDeleted="postIdList => deletePostsLocally(postIdList)"
      @bucketDeleted="path => matchedResults[path.primaryGroup].weeks[path.weekIndex].items.splice(path.bucketIndex, 1)"
      @calendarItemUpdated="updatedCalendarItem => updateCalendarItemLocalValue(updatedCalendarItem)"
      @calendarItemCreated="(primaryGroup, updatedCalendarItem) => calendarItemCreated(primaryGroup, updatedCalendarItem)"
      @manualPostCreated="(primaryGroup, weekGroup, newItemAndPost) => manualPostCreated(primaryGroup, weekGroup, newItemAndPost)"
      @contentMatchUnbound="bucketPath => delete matchedResults[bucketPath.primaryGroup].weeks[bucketPath.weekIndex].items[bucketPath.bucketIndex].contentMatch"
      @contentMatchBound="
        (contentSummary, bucketPath) => {
          matchedResults[bucketPath.primaryGroup].weeks[bucketPath.weekIndex].items[bucketPath.bucketIndex].contentMatch = contentSummary;
        }
      "
      @showFilteredTopics="showFilteredTopics"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import ContentCalendarHelper, { MatchedResultObjBucketPath, SYNC_ACCOUNTS_KEY } from "@logic/ContentCalendarHelper";
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";
import { MetricSender } from "@helpers/MetricSender";
import AdminContext from "@logic/AdminContext";

// Components
// steps
import StepCalendarPosts from "./steps/StepCalendarPosts.vue";

// Types
import {
  CalendarItemGenStatus,
  CalendarPrimaryGroupName,
  ContentCalendarItem,
  ContentCalendarItemMatch,
  ContentCalendarMatchedResults,
  ContentCalendarWeekMatch,
  MarkReadyForPostGenRequest,
  NextFutureCalendarResult,
  SocialOperationType,
  StatusChangeCheckRequest,
} from "@contracts/contentCalendar";
import { SocialMediaPostData } from "@contracts/shareStop";
import { CalendarGenerationStatus } from "./types";
import logger from "@/helpers/Logger";

// Router
const route = useRoute();
const router = useRouter();
const remoteLogger = getGlobalRemoteLogger();

// Normalize route params ------------------------------------------------------
const customerId = computed<string>(() => (Array.isArray(route.params.customerId) ? route.params.customerId[0] : (route.params.customerId as string)));

defineOptions({
  name: "CalendarPosts",
});

// Sync accounts on load =======================================================
const shouldSyncOnLoad = computed(() => globalThis.location.hash === "#" + SYNC_ACCOUNTS_KEY);

// Component lifecycle tracking for polling cleanup ============================
const isComponentMounted = ref<boolean>(true);
const futureCalendarPollTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

onUnmounted(() => {
  isComponentMounted.value = false;
  if (futureCalendarPollTimeoutId.value) {
    clearTimeout(futureCalendarPollTimeoutId.value);
  }
});

onMounted(async () => {
  await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  const hasPosts = await loadMatchedResults();
  if (hasPosts) {
    loadFutureCalendarSummary();
  }
  MetricSender.sendMetric("Cust-Calendar-Topic-PageView", { customerId: customerId.value });
});

// Load match bucket list =====================================================
const matchedResults = ref<ContentCalendarMatchedResults | null>(null);
const hideTopicsWithoutPosts = ref<boolean>(true);

const flatBucketList = computed<Array<ContentCalendarItemMatch>>(() => {
  if (!matchedResults.value) {
    return [];
  }
  return [...(matchedResults.value?.[CalendarPrimaryGroupName.Past].weeks || []), ...(matchedResults.value?.[CalendarPrimaryGroupName.Upcoming].weeks || [])]
    .flat()
    .map((week: ContentCalendarWeekMatch) => week.items)
    .flat();
});

const currentGenerationStatus = ref<CalendarGenerationStatus>(CalendarGenerationStatus.None);
const isLoadingMatchedResults = ref<boolean>(true);
const isCalendarStatusCheckLoopRunning = ref<boolean>(false);
const isPostStatusCheckLoopRunning = ref<boolean>(false);
const noCalendarsFound = ref<boolean>(true); // prevents the main components from loading until this check is done

// Future calendar summary with polling ========================================
const futureCalendarSummary = ref<NextFutureCalendarResult | null>(null);
const SUMMARY_POLL_INTERVAL = 30000; // 30 seconds

async function loadFutureCalendarSummary() {
  if (!isComponentMounted.value) return;

  const result = await ContentCalendarRepo.getNextFutureCalendarSummary(customerId.value);
  futureCalendarSummary.value = result;

  // Continue polling if either is still calculating
  const isNextCalculating = result.nextCalendarGenSummary?.status !== "Done";
  const isLongTermCalculating = result.longTermCalendarGenSummary?.status !== "Done";

  if ((isNextCalculating || isLongTermCalculating) && isComponentMounted.value) {
    futureCalendarPollTimeoutId.value = setTimeout(loadFutureCalendarSummary, SUMMARY_POLL_INTERVAL);
  }
}

// Check if customer has calendars ============================================
async function checkHasCalendars(): Promise<boolean> {
  const calendars = await ContentCalendarRepo.getCalendarsForCustomer(customerId.value);
  return calendars && calendars.length > 0;
}

async function loadMatchedResults(): Promise<boolean> {
  if (!isComponentMounted.value) return;

  isLoadingMatchedResults.value = true;

  // Check for calendars first
  noCalendarsFound.value = !(await checkHasCalendars());
  if (noCalendarsFound.value) {
    isLoadingMatchedResults.value = false;
    return false;
  }

  let result: ContentCalendarMatchedResults | null = null;

  // Loads the Topic and Post data
  result = await ContentCalendarRepo.getAllItemsForCustomer(customerId.value, true, shouldSyncOnLoad.value, hideTopicsWithoutPosts.value);
  // Need to set this since the flatBucketList check below relies on it
  matchedResults.value = result;

  // Check the currentOperation and shortcut if InitialSetup
  if (checkAndRedirectIfNeedInitialContent(result.currentOperation)) {
    return false;
  }
  if (result.currentOperation === SocialOperationType.InitialSetup || result.currentOperation === SocialOperationType.NewContentGen) {
    remoteLogger.info(`${result.currentOperation} for ${customerId.value}, starting calendar operation status loop`);
    isLoadingMatchedResults.value = false;
    await startCalendarOperationStatusLoop();
    return false;
  }

  // Check if there aren't any topics yet
  if (!flatBucketList.value?.length) {
    remoteLogger.info(`No Topics exist yet for ${customerId.value}, calling initiateCustomerContextAndGenerateCalendar`);
    currentGenerationStatus.value = CalendarGenerationStatus.GeneratingCalendar;
    // Call the new setup and generate API (background job)
    await ContentCalendarRepo.initiateCustomerContextAndGenerateCalendar(customerId.value);
    MetricSender.sendMetric("Cust-Calendar-Setup-And-Generate", { customerId: customerId.value });

    // Start polling for calendar operation status
    await startCalendarOperationStatusLoop();
    return false;
  }

  matchedResults.value = result;
  sortMatchedResultBucketsByCalendarItemOrder(matchedResults);

  isLoadingMatchedResults.value = false;

  await startCalendarOperationStatusLoop();
  // Purposely not awaiting
  startPostGenStatusChecksLoop();

  return true;
}

// Regenerate match bucket list ===============================================
async function deleteAndRegenCalendar(_userGuidance: string, _deleteLiveCalendarPosts: boolean) {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });

  isLoadingMatchedResults.value = true;
  currentGenerationStatus.value = CalendarGenerationStatus.GeneratingCalendar;

  // Delete all existing calendar items (always pass true)
  const deleteMessage = await ContentCalendarRepo.deleteAllItems(customerId.value, true);
  if (deleteMessage) {
    alert(deleteMessage);
  }

  // Initiate customer context and generate new calendar (background job)
  await ContentCalendarRepo.initiateCustomerContextAndGenerateCalendar(customerId.value);

  // Start polling for calendar operation status
  await startCalendarOperationStatusLoop();
}

// Sort matchedResult buckets by calendarItem order ===========================
function sortBucketByCalendarItemOrder(bucketA: ContentCalendarItemMatch, bucketB: ContentCalendarItemMatch): number {
  return bucketA.calendarItem.order - bucketB.calendarItem.order;
}

function sortMatchedResultBucketsByCalendarItemOrder(_matchedResults: Ref<ContentCalendarMatchedResults>) {
  _matchedResults.value[CalendarPrimaryGroupName.Upcoming].weeks.forEach(week => {
    week.items.sort(sortBucketByCalendarItemOrder);
  });
  _matchedResults.value[CalendarPrimaryGroupName.Past].weeks.forEach(week => {
    week.items.sort(sortBucketByCalendarItemOrder);
  });
}

// Generate posts =============================================================
async function handleRequestPostGenerationEvent(specificItemId: string) {
  await requestPostsGeneration(specificItemId);

  await startPostGenStatusChecksLoop();
}

const isRequestingPostsGeneration = ref<boolean>(false);
const isGeneratingPosts = ref<boolean>(false);

async function requestPostsGeneration(specificItemId: string) {
  // Update: This only passes specific items now (not weekKey), either for a new post or regen of an existing post
  isRequestingPostsGeneration.value = true;
  isGeneratingPosts.value = true; // being set to false on the last gen status check only

  const request: MarkReadyForPostGenRequest = {
    customerId: customerId.value,
    calendarItemIds: [specificItemId],
  };

  await ContentCalendarRepo.markCalendarItemsReadyForGen(request);

  isRequestingPostsGeneration.value = false;
}

// Check Calendar operation status ===========================================
const StatusCheckTimeout = 10000;

function checkAndRedirectIfNeedInitialContent(currentOperation: SocialOperationType): boolean {
  if (currentOperation === SocialOperationType.NeedInitialContent) {
    remoteLogger.info(`Need initial content for ${customerId.value}, redirecting to upload page`);
    router.push({ name: "CalendarUploadContent", params: { customerId: route.params.customerId } });
    return true;
  }
  return false;
}

async function startCalendarOperationStatusLoop() {
  // Prevent multiple concurrent loops or running after unmount
  if (isCalendarStatusCheckLoopRunning.value || !isComponentMounted.value) {
    return;
  }

  isCalendarStatusCheckLoopRunning.value = true;

  const statusResult = await ContentCalendarRepo.getOperationStatus(customerId.value);

  // Check if component was unmounted during the API call
  if (!isComponentMounted.value) {
    isCalendarStatusCheckLoopRunning.value = false;
    return;
  }

  // Check if we need to redirect to upload initial content
  if (checkAndRedirectIfNeedInitialContent(statusResult.currentOperation)) {
    isCalendarStatusCheckLoopRunning.value = false;
    return;
  }
  // If the setup operation is still running, wait and check again
  if (statusResult.currentOperation === SocialOperationType.InitialSetup) {
    currentGenerationStatus.value = CalendarGenerationStatus.GeneratingCalendar;
    logger.info(`CalendarOperationStatusLoop: Operation still running (${statusResult.currentOperation})`);
    await new Promise(resolve => setTimeout(resolve, StatusCheckTimeout));
    isCalendarStatusCheckLoopRunning.value = false;
    if (isComponentMounted.value) await startCalendarOperationStatusLoop();
    return;
  }
  if (statusResult.currentOperation === SocialOperationType.NewContentGen) {
    currentGenerationStatus.value = CalendarGenerationStatus.GeneratingContentSummaries;
    logger.info(`CalendarOperationStatusLoop: Operation still running (${statusResult.currentOperation})`);
    await new Promise(resolve => setTimeout(resolve, StatusCheckTimeout));
    isCalendarStatusCheckLoopRunning.value = false;
    if (isComponentMounted.value) await startCalendarOperationStatusLoop();
    return;
  }

  // Operation completed, now check if we need to show content upload step
  logger.info(`CalendarOperationStatusLoop: Operation completed, checking inputs`);
  isCalendarStatusCheckLoopRunning.value = false;

  if (!isComponentMounted.value) return;

  // Load the final calendar items
  const result = await ContentCalendarRepo.getAllItemsForCustomer(customerId.value, true, false, hideTopicsWithoutPosts.value);
  matchedResults.value = result;
  sortMatchedResultBucketsByCalendarItemOrder(matchedResults);

  isLoadingMatchedResults.value = false;
  currentGenerationStatus.value = CalendarGenerationStatus.None;

  // Start post-level status checks
  await startPostGenStatusChecksLoop();
}

// Check Calendar items for status change =====================================
async function startPostGenStatusChecksLoop() {
  // Prevent multiple concurrent loops or running after unmount
  if (isPostStatusCheckLoopRunning.value || !isComponentMounted.value) {
    return;
  }

  isPostStatusCheckLoopRunning.value = true;
  const generatingPosts = matchedResults.value[CalendarPrimaryGroupName.Upcoming].weeks
    .map(week => week.items)
    .flat()
    .map(bucket => bucket.calendarItem)
    .filter(calendarItem => calendarItem.genStatus === CalendarItemGenStatus.Pending || calendarItem.genStatus === CalendarItemGenStatus.Generating);
  const itemsWithChangedStatus = await checkCalendarItemsForStatusChange(generatingPosts);

  // Check if component was unmounted during the API call
  if (!isComponentMounted.value) {
    isPostStatusCheckLoopRunning.value = false;
    return;
  }

  itemsWithChangedStatus.forEach(bucket => {
    const path = ContentCalendarHelper.findBucketPath(matchedResults.value, bucket.calendarItem.id);
    matchedResults.value[path.primaryGroup].weeks[path.weekIndex].items[path.bucketIndex] = bucket;
  });

  // Check again if there are any pending or generating posts
  if (generatingPosts.filter(i => i.genStatus === CalendarItemGenStatus.Pending || i.genStatus === CalendarItemGenStatus.Generating).length) {
    logger.info(`PostStatusCheckLoop: Still generating.`);
    await new Promise(resolve => setTimeout(resolve, StatusCheckTimeout));
    isPostStatusCheckLoopRunning.value = false;
    if (isComponentMounted.value) await startPostGenStatusChecksLoop();
  } else {
    logger.info(`PostStatusCheckLoop: Finished.`);
    isGeneratingPosts.value = false;
    isPostStatusCheckLoopRunning.value = false;
  }
}

async function checkCalendarItemsForStatusChange(generatingPosts: Array<ContentCalendarItem>): Promise<Array<ContentCalendarItemMatch>> {
  const itemIdsAndGenStatuses = generatingPosts.map(calendarItem => ({ id: calendarItem.id, genStatus: calendarItem.genStatus }));

  const request: StatusChangeCheckRequest = {
    inputs: itemIdsAndGenStatuses,
  };

  return await ContentCalendarRepo.checkCalendarItemsPostsGenerationStatus(customerId.value, request);
}

// Update calendar item local value ===========================================
function updateCalendarItemLocalValue(updatedItem: ContentCalendarItem) {
  const bucketPath: MatchedResultObjBucketPath = ContentCalendarHelper.findBucketPath(matchedResults.value, updatedItem.id);
  matchedResults.value[bucketPath.primaryGroup].weeks[bucketPath.weekIndex].items[bucketPath.bucketIndex].calendarItem = updatedItem;
}

async function showFilteredTopics() {
  hideTopicsWithoutPosts.value = false;
  await loadMatchedResults();
}

function calendarItemCreated(primaryGroup: CalendarPrimaryGroupName, createdItem: ContentCalendarItem) {
  // Inserts the calendar item in the appropriate location
  const week = matchedResults.value[primaryGroup].weeks.find(w => w.weekGroup === createdItem.weekGroup);
  if (!week) {
    remoteLogger.error(`Week ${createdItem.weekGroup} not found for customer ${createdItem.customerId} ${createdItem.id}`);
    return;
  }
  const newBucket = { calendarItem: createdItem } as unknown as ContentCalendarItemMatch;
  week.items.push(newBucket);

  startPostGenStatusChecksLoop();
}

function manualPostCreated(primaryGroup: CalendarPrimaryGroupName, weekGroup: string, newItemAndPost: ContentCalendarItemMatch) {
  const week = matchedResults.value[primaryGroup].weeks.find(w => w.weekGroup === weekGroup);
  if (!week) {
    remoteLogger.error(`Week ${weekGroup} not found for customer ${customerId.value}`);
    return;
  }
  week.items.push(newItemAndPost);
}

// Update post local value ====================================================
function updatePostsLocally(postList: Array<SocialMediaPostData>, newWeekKey?: string) {
  postList.forEach(updatedPost => {
    // console.info(`Updating post ${updatedPost.id} for ${updatedPost.customerId} to week ${newWeekKey}`);
    if (newWeekKey) {
      movePost(updatedPost, newWeekKey);
    } else {
      const bucketPath: MatchedResultObjBucketPath = ContentCalendarHelper.findBucketPath(matchedResults.value, updatedPost.id);
      matchedResults.value[bucketPath.primaryGroup].weeks[bucketPath.weekIndex].items[bucketPath.bucketIndex].socialPost = updatedPost;
    }
  });
}

// Delete posts locally =======================================================
function deletePostsLocally(postIdList: Array<string>) {
  postIdList.forEach(postId => {
    const bucketPath: MatchedResultObjBucketPath = ContentCalendarHelper.findBucketPath(matchedResults.value, postId);

    delete matchedResults.value[bucketPath.primaryGroup].weeks[bucketPath.weekIndex].items[bucketPath.bucketIndex].socialPost;
  });
}

// Move post to another week ==================================================
function movePost(updatedPost: SocialMediaPostData, newWeekKey: string) {
  remoteLogger.info(`Moving post ${updatedPost.id} for ${updatedPost.customerId} to week ${newWeekKey}`);
  // Find current location of the post
  const currentPath: MatchedResultObjBucketPath = ContentCalendarHelper.findBucketPath(matchedResults.value, updatedPost.id);

  // Remove the bucket from its current location
  const bucket = matchedResults.value[currentPath.primaryGroup].weeks[currentPath.weekIndex].items.splice(currentPath.bucketIndex, 1)[0];

  // Update the calendar item week key locally if present
  if (bucket?.calendarItem) {
    bucket.calendarItem.weekGroup = newWeekKey;
  }

  // Ensure the post is updated
  bucket.socialPost = updatedPost;

  // Find the destination week path (creates the week if it doesn't exist - can happen when rescheduling to a future week)
  const destinationPath: MatchedResultObjBucketPath = ContentCalendarHelper.findWeekPath(matchedResults.value, newWeekKey);
  matchedResults.value[destinationPath.primaryGroup].weeks[destinationPath.weekIndex].items.push(bucket);
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Content calendar new page ==================================================
.content-calendar-new-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__progressbar {
    margin-bottom: 40px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .content-calendar-new-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .content-calendar-new-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .content-calendar-new-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .content-calendar-new-page {
    width: $tablet-content-width;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .content-calendar-new-page {
    width: $mobile-content-width;
  }
}
</style>
